import React from "react";
import { Box, Typography, Paper, Container, Link } from "@mui/material";

const LAST_UPDATED = "April 28, 2026";
const COMPANY_NAME = "Cloud Job Manager";
const PRIVACY_EMAIL = "privacy@cloudjobmanager.com";

const theme = {
  primary: "#0f2b46",
  accent: "var(--amber)",
  textPrimary: "#0f2b46",
  textSecondary: "#444",
  bgPage: "#f8f9fa",
  cardBorder: "1px solid #e0e0e0",
};

const sectionTitleSx = {
  fontWeight: 600,
  color: theme.textPrimary,
  fontSize: "1.25rem",
  mt: 4,
  mb: 1.5,
  paddingBottom: "6px",
  borderBottom: `2px solid ${theme.accent}`,
  display: "inline-block",
};

const bodySx = {
  color: theme.textSecondary,
  lineHeight: 1.75,
  fontSize: "1.05rem",
  mb: 1.5,
};

const listItemSx = {
  ...bodySx,
  mb: 0.5,
  pl: 0,
};

export default function PrivacyPolicy() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: theme.bgPage,
        py: { xs: 4, sm: 6 },
        px: 2,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: "12px",
            border: theme.cardBorder,
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.textPrimary,
              fontSize: { xs: "1.75rem", sm: "2.1rem" },
              paddingBottom: "10px",
              borderBottom: `2px solid ${theme.accent}`,
              display: "inline-block",
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.textSecondary,
              fontSize: "0.95rem",
              mb: 3,
            }}
          >
            Last updated: {LAST_UPDATED}
          </Typography>

          <Typography sx={bodySx}>
            {COMPANY_NAME} ("we", "us", "our") respects your privacy. This
            Privacy Policy explains what information we collect, how we use it,
            with whom we share it, and the choices you have. It applies to our
            websites, applications, and related services (collectively, the
            "Service").
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            1. Information We Collect
          </Typography>
          <Typography sx={bodySx}>
            We collect information in three ways:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" sx={listItemSx}>
              <strong>Information you provide.</strong> Account details (name,
              email, phone), business and customer records you enter, billing
              and payment information, and content you submit through the
              Service.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              <strong>Information collected automatically.</strong> Device and
              browser data, IP address, log data, usage analytics, and
              cookies or similar technologies.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              <strong>Information from third parties.</strong> Authentication
              providers, payment processors, mapping providers, and other
              integrations you choose to connect.
            </Typography>
          </Box>

          <Typography variant="h6" sx={sectionTitleSx}>
            2. How We Use Information
          </Typography>
          <Typography sx={bodySx}>We use information to:</Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" sx={listItemSx}>
              Provide, maintain, and improve the Service.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              Process payments and manage subscriptions.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              Send transactional messages, security alerts, and (with consent
              where required) marketing communications.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              Detect, prevent, and respond to fraud, abuse, and security
              incidents.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              Comply with legal obligations and enforce our terms.
            </Typography>
          </Box>

          <Typography variant="h6" sx={sectionTitleSx}>
            3. Cookies and Similar Technologies
          </Typography>
          <Typography sx={bodySx}>
            We use cookies and similar technologies to keep you signed in,
            remember preferences, measure usage, and improve performance. You
            can control cookies through your browser settings. Disabling
            cookies may limit some features of the Service.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            4. How We Share Information
          </Typography>
          <Typography sx={bodySx}>
            We do not sell your personal information. We share information
            only as needed:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <Typography component="li" sx={listItemSx}>
              <strong>Service providers.</strong> Vendors who host
              infrastructure, process payments, send email, or provide
              analytics, under contracts that require appropriate
              safeguards.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              <strong>Within your organization.</strong> Account
              administrators may access information about users in their
              organization.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              <strong>Legal and safety.</strong> When required by law, legal
              process, or to protect rights, safety, and the integrity of
              the Service.
            </Typography>
            <Typography component="li" sx={listItemSx}>
              <strong>Business transfers.</strong> In connection with a
              merger, acquisition, or sale of assets, subject to standard
              confidentiality protections.
            </Typography>
          </Box>

          <Typography variant="h6" sx={sectionTitleSx}>
            5. Data Retention
          </Typography>
          <Typography sx={bodySx}>
            We retain personal information for as long as needed to provide
            the Service, comply with legal obligations, resolve disputes, and
            enforce agreements. When information is no longer required, we
            delete or anonymize it.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            6. Security
          </Typography>
          <Typography sx={bodySx}>
            We use administrative, technical, and physical safeguards designed
            to protect personal information, including encryption in transit,
            access controls, and monitoring. No method of transmission or
            storage is 100% secure, and we cannot guarantee absolute security.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            7. Your Rights and Choices
          </Typography>
          <Typography sx={bodySx}>
            Depending on your location, you may have the right to access,
            correct, delete, or port your personal information, or to object
            to or restrict certain processing. You can also opt out of
            marketing emails using the unsubscribe link in any message. To
            exercise your rights, contact us at{" "}
            <Link href={`mailto:${PRIVACY_EMAIL}`} sx={{ color: theme.primary }}>
              {PRIVACY_EMAIL}
            </Link>
            .
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            8. International Data Transfers
          </Typography>
          <Typography sx={bodySx}>
            We may process and store information in countries other than your
            own. Where required, we implement appropriate transfer mechanisms
            such as standard contractual clauses to protect personal
            information.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            9. Children's Privacy
          </Typography>
          <Typography sx={bodySx}>
            The Service is not directed to children under 13 (or the
            equivalent minimum age in the relevant jurisdiction), and we do
            not knowingly collect personal information from them. If you
            believe a child has provided us with personal information, please
            contact us so we can delete it.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            10. Changes to This Policy
          </Typography>
          <Typography sx={bodySx}>
            We may update this Privacy Policy from time to time. We will post
            the updated version with a new "Last updated" date and, for
            material changes, provide additional notice through the Service or
            by email.
          </Typography>

          <Typography variant="h6" sx={sectionTitleSx}>
            11. Contact Us
          </Typography>
          <Typography sx={bodySx}>
            For questions about this Privacy Policy or our privacy practices,
            contact us at{" "}
            <Link href={`mailto:${PRIVACY_EMAIL}`} sx={{ color: theme.primary }}>
              {PRIVACY_EMAIL}
            </Link>
            .
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
